import type { ReactNode } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { LogoutCurve, Moon, Monitor, Setting2, Sun1 } from 'iconsax-reactjs';
import { useTheme } from '@/lib/useTheme';
import { useAuthStatus, useLogout } from '@/lib/useAuth';
import { Button } from '@/components/ui/Button';
import { Dock } from '@/components/Dock';
import { Logo } from '@/components/Logo';
import { AlertStack } from '@/components/AlertStack';
import { FeedBanner } from '@/components/FeedBanner';
import { LiveStream } from '@/components/LiveStream';
import { LivePrice } from '@/components/LivePrice';
import { SymbolPicker } from '@/components/SymbolPicker';
import { cn } from '@/lib/utils';
import { useI18n } from '@/lib/i18n';

/**
 * Chrome that wraps every signed-in screen: top bar, the page itself, the dock.
 *
 * Content is padded at the bottom by more than the dock's height so the dock never
 * covers the end of a scrolling page.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const { choice, toggle } = useTheme();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { data: status } = useAuthStatus();
  const logout = useLogout();
  const { t } = useI18n();

  return (
    <div className="flex min-h-dvh flex-col">
      {/* Glass, with a gold hairline along its bottom edge. */}
      <header className="sticky top-0 z-30 border-b border-transparent bg-background/55 shadow-[0_1px_0_0_color-mix(in_oklch,var(--primary)_22%,transparent),0_10px_30px_-24px_color-mix(in_oklch,var(--primary)_60%,transparent)] backdrop-blur-xl backdrop-saturate-150">
        <div className="mx-auto flex h-14 w-full max-w-[1600px] items-center gap-3 px-4 sm:px-6">
          <Logo className="h-6" />
          <LivePrice />

          <div className="ms-auto flex items-center gap-2">
            <SymbolPicker />

            {status?.username && (
              <span className="me-2 hidden text-sm text-muted-foreground sm:inline">
                {status.username}
              </span>
            )}

            <Link
              to="/settings"
              aria-label={t('shell.settings')}
              aria-current={pathname.startsWith('/settings') ? 'page' : undefined}
              title={t('shell.settings')}
              className={cn(
                'flex size-10 items-center justify-center rounded-lg transition-colors',
                pathname.startsWith('/settings')
                  ? 'bg-primary/12 text-primary'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground',
              )}
            >
              <Setting2 size={19} />
            </Link>

            <Button
              variant="ghost"
              size="icon"
              onClick={toggle}
              /*
               * Label, title and icon all describe what the NEXT press gives,
               * which is how the two-state button already read. The cycle is
               * dark, light, then follow-the-device.
               */
              aria-label={
                choice === 'dark'
                  ? t('shell.switchToLight')
                  : choice === 'light'
                    ? t('shell.switchToSystem')
                    : t('shell.switchToDark')
              }
              title={
                choice === 'dark'
                  ? t('shell.lightTheme')
                  : choice === 'light'
                    ? t('shell.systemTheme')
                    : t('shell.darkTheme')
              }
            >
              {choice === 'dark' ? (
                <Sun1 size={19} />
              ) : choice === 'light' ? (
                <Monitor size={19} />
              ) : (
                <Moon size={19} />
              )}
            </Button>

            <Button
              variant="ghost"
              size="icon"
              onClick={() => logout.mutate()}
              disabled={logout.isPending}
              aria-label={t('shell.signOut')}
              title={t('shell.signOut')}
            >
              <LogoutCurve size={19} />
            </Button>
          </div>
        </div>
        {/* Inside the sticky header, so they stay in view while scrolling. */}
        <FeedBanner />
        <AlertStack />
      </header>

      <main className="mx-auto w-full max-w-[1600px] flex-1 px-4 pt-6 pb-28 sm:px-6">
        {children}
      </main>

      <Dock />
      <LiveStream />
    </div>
  );
}
